import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { TRANSPORT_STATUS_COLORS } from '../../constants/constants';
import { TransportListItem } from '../../reducers/transport';

interface Props {
    data: any[];
    onEdit: (item: any) => void;
    mode?: 'transport' | 'waiting' | 'withoutTransport';
}

const hashColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#1e3a5f', '#2a5a8a', '#0891b2', '#7c3aed', '#059669', '#d97706', '#dc2626'];
    return colors[Math.abs(hash) % colors.length];
};

const fmtDate = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
};

const TransportCardGrid: React.FC<Props> = ({ data, onEdit, mode = 'transport' }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    if (!data || data.length === 0) {
        return (
            <div className="ccg-grid">
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    <span style={{ marginTop: 12, fontSize: 13 }}><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></span>
                </div>
            </div>
        );
    }

    if (mode === 'waiting') {
        return (
            <div className="ccg-grid">
                {data.map((item: any) => {
                    const label = item.label || item.warehouseLabel || '';
                    const initials = label.substring(0, 2).toUpperCase() || 'WR';
                    const clientName = item.client?.name || '';
                    const projectName = item.project?.name || '';
                    const city = item.client?.city || '';
                    const country = item.client?.country || '';
                    const productCount = item.products?.length || 0;

                    return (
                        <MikaCard
                            key={item.id}
                            onClick={() => onEdit(item)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: hashColor(label) }}>{initials}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="ccg-name">{label}</div>
                                        <div className="ccg-company">{clientName}</div>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#1e3a5f', background: '#dbeafe' }}>
                                        {fm('transport.card.productsCount', '{count} produkte', { count: productCount })}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{projectName || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="transport.col.city" defaultMessage="Qyteti" /></span>
                                            <span className="ccg-info-value">{city || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="transport.col.country" defaultMessage="Shteti" /></span>
                                            <span className="ccg-info-value">{country || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">{fmtDate(item.createdAt || item.created_at)}</span>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    if (mode === 'withoutTransport') {
        return (
            <div className="ccg-grid">
                {data.map((item: any) => {
                    const label = item.label || '';
                    const initials = label.substring(0, 2).toUpperCase() || 'WR';
                    const isCompleted = item.transport_status === 'completed';

                    return (
                        <MikaCard
                            key={item.id}
                            onClick={() => onEdit(item)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: hashColor(label) }}>{initials}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="ccg-name">{label}</div>
                                        <div className="ccg-company">{item.client_name || '-'}</div>
                                    </div>
                                    <span className="ccg-status-badge" style={{
                                        color: isCompleted ? '#16a34a' : '#d97706',
                                        background: isCompleted ? '#f0fdf4' : '#fffbeb',
                                    }}>
                                        {isCompleted
                                            ? <FormattedMessage id="transport.status.completed" defaultMessage="E përfunduar" />
                                            : <FormattedMessage id="transport.status.waiting" defaultMessage="Në pritje" />}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{item.project_name || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="transport.col.city" defaultMessage="Qyteti" /></span>
                                            <span className="ccg-info-value">{item.client_city || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="transport.col.country" defaultMessage="Shteti" /></span>
                                            <span className="ccg-info-value">{item.client_country || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">{fmtDate(item.created_at)}</span>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    // Default: transport mode
    return (
        <div className="ccg-grid">
            {data.map((item: TransportListItem) => {
                const statusColors = TRANSPORT_STATUS_COLORS[item.status] || { color: '#94a3b8', bg: '#f1f5f9' };
                const initials = item.code ? item.code.substring(0, 2).toUpperCase() : 'TR';
                const bgColor = hashColor(item.code || '');

                return (
                    <MikaCard
                        key={item.id}
                        onClick={() => onEdit(item)}
                        header={
                            <div className="ccg-card-header">
                                <div className="ccg-avatar" style={{ background: bgColor }}>
                                    {initials}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="ccg-name">{item.code ? item.code.substring(0, 8) : '-'}</div>
                                    <div className="ccg-company">{item.driver?.name || fm('transport.driver.none', 'Pa shofer')}</div>
                                </div>
                                <span className="ccg-status-badge" style={{
                                    color: statusColors.color,
                                    background: statusColors.bg,
                                }}>
                                    {item.status === 1
                                        ? fm('transport.strip.inTransit', 'Në transport')
                                        : item.status === 2
                                            ? fm('transport.statusRow.delivered', 'Dërguar')
                                            : (item.statusLabel || '')}
                                </span>
                            </div>
                        }
                        body={
                            <div className="ccg-card-body-content">
                                <div className="ccg-info-grid">
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                        <span className="ccg-info-value">{item.clientNames || '-'}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                        <span className="ccg-info-value">{item.projectNames || '-'}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="transport.col.items" defaultMessage="Artikuj" /></span>
                                        <span className="ccg-info-value">{item.itemCount}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="transport.col.destination" defaultMessage="Destinacioni" /></span>
                                        <span className="ccg-info-value">{item.address || '-'}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="transport.col.departure" defaultMessage="Nisja" /></span>
                                        <span className="ccg-info-value">
                                            {item.departureDate ? fmtDate(item.departureDate) : '-'}
                                        </span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label"><FormattedMessage id="transport.col.arrival" defaultMessage="Arritja" /></span>
                                        <span className="ccg-info-value">
                                            {item.arrivalDate ? fmtDate(item.arrivalDate) : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        }
                        footer={
                            <div className="ccg-card-footer">
                                <span className="ccg-date">
                                    {item.createdAt ? fmtDate(item.createdAt) : ''}
                                </span>
                                <div className="ccg-footer-actions">
                                    <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onEdit(item); }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        }
                    />
                );
            })}
        </div>
    );
};

export default TransportCardGrid;
